// Live chat: connection pill, autoscrolling message list, composer (or login prompt for anon).
// Socket data is supplied by the parent (StreamPage) so the danmaku overlay and this list share
// a single WebSocket connection.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessagesSquare, SendHorizontal } from "lucide-react";
import { useMe } from "../api/hooks.js";
import { useLoginModal } from "../ui/loginModal.js";
import type { ConnState } from "../ws/cborSocket.js";
import type { ChatLine } from "../ws/useStreamSocket.js";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button.js";
import { ChatMessage } from "./ChatMessage.js";

const CONN_CLS = {
  connecting: "bg-warn/15 text-warn",
  connected: "bg-success/15 text-success",
  disconnected: "bg-destructive/15 text-destructive-soft",
} as const;

export interface ChatPanelProps {
  messages: ChatLine[];
  conn: ConnState;
  sysNotice: string | null;
  sendChat: (body: string) => void;
  fetchOlder: () => void;
}

export function ChatPanel({ messages, conn, sysNotice, sendChat, fetchOlder }: ChatPanelProps) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { openLogin } = useLoginModal();
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);

  // Track whether the user is pinned to the bottom (so we don't yank them up while reading).
  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (el.scrollTop < 40) fetchOlder();
  };

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Surface sysNotice as a transient banner.
  useEffect(() => {
    if (!sysNotice) return;
    setNotice(sysNotice);
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [sysNotice]);

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    sendChat(body);
    setDraft("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-card/60 backdrop-blur-md">
      <div className="flex items-center justify-between border-b border-white/[0.06] px-3.5 py-2.5">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessagesSquare className="h-4 w-4 text-muted-foreground" />
          {t("chat.title")}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            CONN_CLS[conn],
          )}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
          {t(`chat.${conn}`)}
        </span>
      </div>

      {notice && (
        <div className="border-b border-white/[0.06] bg-warn/10 px-3.5 py-1.5 text-xs text-warn">
          {notice}
        </div>
      )}

      <div
        ref={listRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto py-2 scrollbar-thin"
      >
        {messages.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground/70">
            {t("chat.empty")}
          </div>
        ) : (
          messages.map((line) => <ChatMessage key={line.id} line={line} />)
        )}
      </div>

      <div className="border-t border-white/[0.06] p-3">
        {me ? (
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={t("chat.placeholder")}
              className="max-h-28 min-h-[38px] flex-1 resize-none rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-foreground shadow-inner shadow-black/20 outline-none transition placeholder:text-muted-foreground/90 focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/40"
            />
            <Button
              variant="primary"
              size="icon"
              onClick={send}
              disabled={!draft.trim()}
              aria-label={t("chat.send")}
            >
              <SendHorizontal className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button variant="outline" className="w-full" onClick={openLogin}>
            {t("chat.loginToComment")}
          </Button>
        )}
      </div>
    </div>
  );
}
